import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchExerciseById,
  fetchUserExercises,
  fetchFilteredExercises,
  createExercise,
  createExerciseFromForm,
  editExercise,
  editExerciseFromForm,
  removeExercise,
  uploadExercisePhotoThunk,
  deleteExercisePhotoThunk,
  clearCurrentExercise,
  clearExercisesError,
  updateExerciseCommentCount,
  updateExerciseInState
} from '../store/slices/exerciseSlice';
import { Exercise, ExerciseFormData, ExerciseType } from '../types/exercise';
import { subscribeToExercise } from '../services/firebase/exercise.service';

/**
 * Custom hook for working with exercises
 */
export const useExercise = () => {
  const dispatch = useAppDispatch();
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<(() => void) | null>(null);

  // Get the exercise state from Redux
  const {
    exercises,
    userExercises,
    currentExercise,
    isLoading: storeLoading,
    error: storeError
  } = useAppSelector((state) => state.exercise);

  // Get user ID for current user
  const { user } = useAppSelector((state) => state.auth);

  /**
   * Cleanup subscription on unmount
   */
  useEffect(() => {
    return () => {
      if (currentSubscription) {
        currentSubscription();
      }
    };
  }, [currentSubscription]);

  /**
   * Get an exercise by ID
   */
  const getExerciseById = useCallback(async (ownerId: string, exerciseId: string) => {
    try {
      setLocalLoading(true);
      const result = await dispatch(fetchExerciseById({ ownerId, exerciseId })).unwrap();
      setLocalLoading(false);
      return result;
    } catch (error: any) {
      setLocalError(error.message || 'Failed to fetch exercise');
      setLocalLoading(false);
      throw error;
    }
  }, [dispatch]);

  /**
   * Subscribe to real-time updates for an exercise
   */
  const subscribeToExerciseUpdates = useCallback((ownerId: string, exerciseId: string) => {
    // Unsubscribe from any existing subscription
    if (currentSubscription) {
      currentSubscription();
    }

    // Create new subscription
    const unsubscribe = subscribeToExercise(ownerId, exerciseId, (exercise) => {
      // Update current exercise in Redux
      dispatch(updateExerciseInState(exercise));
    });

    // Store unsubscribe function
    setCurrentSubscription(() => unsubscribe);

    // Fetch initial data
    dispatch(fetchExerciseById({ ownerId, exerciseId }));

    // Return unsubscribe function
    return unsubscribe;
  }, [dispatch, currentSubscription]);

  /**
   * Get all exercises for a user
   */
  const getUserExercisesList = useCallback(async (userId: string) => {
    try {
      setLocalLoading(true);
      const result = await dispatch(fetchUserExercises(userId)).unwrap();
      setLocalLoading(false);
      return result;
    } catch (error: any) {
      setLocalError(error.message || 'Failed to fetch user exercises');
      setLocalLoading(false);
      throw error;
    }
  }, [dispatch]);

  /**
   * Get filtered exercises
   */
  const getFilteredExercises = useCallback(async (
    userId: string,
    options: { type?: ExerciseType; limit?: number; startDate?: number } = {}
  ) => {
    try {
      setLocalLoading(true);
      const result = await dispatch(fetchFilteredExercises({ userId, ...options })).unwrap();
      setLocalLoading(false);
      return result;
    } catch (error: any) {
      setLocalError(error.message || 'Failed to fetch filtered exercises');
      setLocalLoading(false);
      throw error;
    }
  }, [dispatch]);

  /**
   * Create a new exercise using the raw exercise data
   */
  const addNewExercise = useCallback(async (
    exerciseData: Omit<Exercise, 'exerciseKey' | 'author'>
  ) => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return null;
    }

    try {
      setLocalLoading(true);
      const result = await dispatch(createExercise({
        userId: user.uid,
        exerciseData
      })).unwrap();
      setLocalLoading(false);
      return result.exerciseId;
    } catch (error: any) {
      setLocalError(error.message || 'Failed to create exercise');
      setLocalLoading(false);
      throw error;
    }
  }, [dispatch, user?.uid]);

  /**
   * Create a new exercise from form data
   */
  const createExerciseFromFormData = useCallback(async (formData: ExerciseFormData) => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return null;
    }

    try {
      setLocalLoading(true);
      const result = await dispatch(createExerciseFromForm({
        userId: user.uid,
        formData
      })).unwrap();
      setLocalLoading(false);
      return result.exerciseId;
    } catch (error: any) {
      setLocalError(error.message || 'Failed to create exercise from form data');
      setLocalLoading(false);
      throw error;
    }
  }, [dispatch, user?.uid]);

  /**
   * Update an existing exercise with raw data
   */
  const updateExistingExercise = useCallback(async (
    exerciseId: string,
    updatedData: Partial<Exercise>
  ) => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return;
    }

    try {
      setLocalLoading(true);
      await dispatch(editExercise({
        userId: user.uid,
        exerciseId,
        updatedData
      })).unwrap();
      setLocalLoading(false);
    } catch (error: any) {
      setLocalError(error.message || 'Failed to update exercise');
      setLocalLoading(false);
      throw error;
    }
  }, [dispatch, user?.uid]);

  /**
   * Update an exercise from form data
   */
  const updateExerciseFromFormData = useCallback(async (
    exerciseId: string,
    formData: Partial<ExerciseFormData>
  ) => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return;
    }

    try {
      setLocalLoading(true);
      await dispatch(editExerciseFromForm({
        userId: user.uid,
        exerciseId,
        formData
      })).unwrap();
      setLocalLoading(false);
    } catch (error: any) {
      setLocalError(error.message || 'Failed to update exercise from form data');
      setLocalLoading(false);
      throw error;
    }
  }, [dispatch, user?.uid]);

  /**
   * Delete an exercise
   */
  const deleteExistingExercise = useCallback(async (exerciseId: string) => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return;
    }

    try {
      setLocalLoading(true);
      await dispatch(removeExercise({
        userId: user.uid,
        exerciseId
      })).unwrap();
      setLocalLoading(false);
    } catch (error: any) {
      setLocalError(error.message || 'Failed to delete exercise');
      setLocalLoading(false);
      throw error;
    }
  }, [dispatch, user?.uid]);

  // Like functionality removed - use the unified useLikes hook instead

  /**
   * Upload a photo for an exercise
   */
  const uploadPhoto = useCallback(async (
    exerciseId: string,
    photoFile: File
  ) => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return null;
    }

    try {
      setLocalLoading(true);
      const result = await dispatch(uploadExercisePhotoThunk({
        userId: user.uid,
        exerciseId,
        photoFile
      })).unwrap();
      setLocalLoading(false);
      return result.photo;
    } catch (error: any) {
      setLocalError(error.message || 'Failed to upload exercise photo');
      setLocalLoading(false);
      throw error;
    }
  }, [dispatch, user?.uid]);

  /**
   * Delete a photo from an exercise
   */
  const deletePhoto = useCallback(async (
    exerciseId: string,
    photoId: string
  ) => {
    if (!user?.uid) {
      setLocalError('User not authenticated');
      return;
    }

    try {
      setLocalLoading(true);
      await dispatch(deleteExercisePhotoThunk({
        userId: user.uid,
        exerciseId,
        photoId
      })).unwrap();
      setLocalLoading(false);
    } catch (error: any) {
      setLocalError(error.message || 'Failed to delete exercise photo');
      setLocalLoading(false);
      throw error;
    }
  }, [dispatch, user?.uid]);

  /**
   * Update comment count for an exercise
   */
  const updateCommentCount = useCallback((exerciseId: string, count: number) => {
    dispatch(updateExerciseCommentCount({ exerciseId, count }));
  }, [dispatch]);

  /**
   * Clear the current exercise from state
   */
  const clearExercise = useCallback(() => {
    dispatch(clearCurrentExercise());

    // Unsubscribe from any existing subscription
    if (currentSubscription) {
      currentSubscription();
      setCurrentSubscription(null);
    }
  }, [dispatch, currentSubscription]);

  /**
   * Function to clear errors
   */
  const clearError = useCallback(() => {
    setLocalError(null);
    dispatch(clearExercisesError());
  }, [dispatch]);

  /**
   * Get exercises for the current user
   */
  const getCurrentUserExercises = useCallback(async () => {
    if (!user?.uid) return [];

    if (!userExercises[user.uid]) {
      await getUserExercisesList(user.uid);
    }

    return (userExercises[user.uid] || [])
      .map(id => exercises[id])
      .filter(Boolean); // Filter out any undefined values
  }, [user, userExercises, exercises, getUserExercisesList]);

  /**
   * Get exercises by type for the current user
   */
  const getCurrentUserExercisesByType = useCallback(async (type: ExerciseType) => {
    if (!user?.uid) return [];

    await getFilteredExercises(user.uid, { type });

    return (userExercises[user.uid] || [])
      .map(id => exercises[id])
      .filter(exercise => exercise?.type === type);
  }, [user, userExercises, exercises, getFilteredExercises]);

  /**
   * Get recent exercises for the current user
   */
  const getRecentExercises = useCallback(async (limit: number = 10) => {
    if (!user?.uid) return [];

    await getFilteredExercises(user.uid, { limit });

    return (userExercises[user.uid] || [])
      .map(id => exercises[id])
      .filter(Boolean) // Filter out any undefined values
      .sort((a, b) => b.date - a.date)
      .slice(0, limit);
  }, [user, userExercises, exercises, getFilteredExercises]);

  return {
    // State
    exercises,
    userExercises,
    currentExercise,
    isLoading: localLoading || storeLoading,
    error: localError || storeError,

    // Core functions
    getExerciseById,
    subscribeToExerciseUpdates,
    getUserExercises: getUserExercisesList,
    getFilteredExercises,
    addNewExercise,
    createExerciseFromFormData,
    updateExistingExercise,
    updateExerciseFromFormData,
    deleteExistingExercise,
    // Like functionality removed - use the unified useLikes hook instead
    uploadPhoto,
    deletePhoto,
    updateCommentCount,
    clearExercise,
    clearError,

    // Convenience methods
    getCurrentUserExercises,
    getCurrentUserExercisesByType,
    getRecentExercises
  };
};